import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserPlus, ArrowRight } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Avatar } from '@/components/ui/Avatar';
import { Banner } from '@/components/ui/Banner';
import { Button } from '@/components/ui/Button';
import { ListRow } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { MenuItem } from '@/components/ui/Popover';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { Select } from '@/components/ui/Select';
import { SlideOver, SlideOverFooter } from '@/components/ui/SlideOver';
import { StatusChip } from '@/components/ui/Chip';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { ConfirmActionModal, PatientTabMoreMenu } from '@/components/patient/PatientTabActions';
import { PatientTabFrame } from '@/components/patient/PatientTabFrame';
import { useAuth } from '@/lib/auth';
import { useCreatePatient, useUpdatePatient } from '@/hooks/usePatients';
import {
  usePatientPicker,
  useRelationPersonSearch,
  type PatientPickerItem,
} from '@/hooks/usePatientPicker';
import {
  usePatientRelations,
  useCreatePatientRelation,
  useDeletePatientRelation,
  type PatientRelationRow,
} from '@/hooks/usePatientRelations';
import { useClinicContacts, useCreateClinicContact } from '@/hooks/useClinicContacts';
import {
  PERSON_RELATION_TYPES,
  ORG_RELATION_TYPES,
  contactKindFromOrgRelation,
  expectedSexForPersonRelation,
  keepSelectedRelationPerson,
  type OrgRelationType,
} from '@/lib/patientRelations';
import { isAcceptableCpr } from '@/lib/cpr';
import { contactFieldErrorTexts } from '@/lib/contactFields';
import {
  DEFAULT_TELEPHONE_CODE,
  focusPatientField,
  formatPatientTelephoneNumbers,
} from '@/lib/patientStamkortFields';

interface RelationsTabProps {
  patientId: string;
  clinicId: string;
}

export function RelationsTab({ patientId, clinicId }: RelationsTabProps) {
  const { t } = useTranslation();
  const { data: relations, isLoading, error } = usePatientRelations(patientId);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<PatientRelationRow | null>(null);

  const rows = relations ?? [];
  const empty = !isLoading && !error && rows.length === 0;

  return (
    <>
      <PatientTabFrame
        title={t('patients.tabs.relations')}
        count={rows.length}
        action={
          <Button type="button" onClick={() => setShowCreate(true)}>
            {t('patients.relations.add')}
          </Button>
        }
        loading={isLoading}
        error={error ? t('patients.relations.error_loading') : undefined}
        empty={
          empty
            ? {
                icon: <UserPlus className="h-7 w-7" />,
                title: t('patients.relations.empty_title'),
                description: t('patients.relations.empty_description'),
              }
            : undefined
        }
      >
        <div className="mt-s4">
          {rows.map((row) => (
            <RelationRow
              key={row.id}
              relation={row}
              patientId={patientId}
              onEditPerson={() => setEditing(row)}
            />
          ))}
        </div>
      </PatientTabFrame>

      {showCreate && (
        <AddRelationModal
          patientId={patientId}
          clinicId={clinicId}
          onClose={() => setShowCreate(false)}
        />
      )}

      {editing && editing.to_patient_id && (
        <EditRelatedPersonModal relation={editing} onClose={() => setEditing(null)} />
      )}
    </>
  );
}

function RelationRow({
  relation,
  patientId,
  onEditPerson,
}: {
  relation: PatientRelationRow;
  patientId: string;
  onEditPerson: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const remove = useDeletePatientRelation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const isPerson = relation.target_kind === 'person';
  const isActivePatient = relation.target_patient_status === 'active';
  const name = relation.target_name ?? '';

  const handleDelete = async () => {
    try {
      await remove.mutateAsync({
        relation_id: relation.id,
        patient_id: patientId,
        related_patient_id: relation.to_patient_id,
      });
      toast.success(t('patients.relations.toast_deleted'));
      setConfirmOpen(false);
    } catch {
      toast.error(t('patients.relations.error_delete'));
    }
  };

  return (
    <ListRow>
      <Avatar name={name} size={40} />
      <div className="min-w-0 flex-1">
        <p className={cn(TYPE.body, 'font-semibold text-ds-text')}>{name}</p>
        <p className={cn(TYPE.small, 'mt-s1 truncate text-ds-muted')}>
          {[relation.target_phone, relation.target_email, relation.notes]
            .filter(Boolean)
            .join(' · ') || t('patients.relations.no_contact')}
        </p>
      </div>
      <div className="flex flex-wrap items-center justify-end gap-s2">
        <StatusChip tone="neutral">
          {t(`patients.relations.types.${relation.relation_type}`, relation.relation_type)}
        </StatusChip>
        {isPerson ? (
          <StatusChip tone="upcoming">{t('patients.relations.badge_person')}</StatusChip>
        ) : (
          <StatusChip tone="draft">{t('patients.relations.badge_org')}</StatusChip>
        )}
        {isActivePatient ? (
          <StatusChip tone="completed">{t('patients.relations.badge_patient')}</StatusChip>
        ) : null}
        {isPerson && isActivePatient && relation.to_patient_id ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/patients/${relation.to_patient_id}`)}
            aria-label={t('patients.see_details')}
          >
            <ArrowRight className="h-4 w-4" />
          </Button>
        ) : null}
        <PatientTabMoreMenu label={t('patients.tab_frame.more_actions_for', { name })}>
          {isPerson && !isActivePatient ? (
            <MenuItem onClick={onEditPerson}>{t('common.edit')}</MenuItem>
          ) : null}
          <MenuItem danger onClick={() => setConfirmOpen(true)}>
            {t('patients.relations.remove')}
          </MenuItem>
        </PatientTabMoreMenu>
      </div>
      <ConfirmActionModal
        open={confirmOpen}
        title={t('patients.relations.confirm_delete')}
        confirmLabel={t('patients.relations.remove')}
        pending={remove.isPending}
        onConfirm={() => void handleDelete()}
        onClose={() => setConfirmOpen(false)}
      />
    </ListRow>
  );
}

export function AddRelationModal({
  patientId,
  clinicId,
  onClose,
}: {
  patientId: string;
  clinicId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const canSetCpr = Boolean(hasPermission(clinicId, 'patients_cpr', 'create'));
  const createRelation = useCreatePatientRelation();
  const createPatient = useCreatePatient();
  const createContact = useCreateClinicContact(clinicId);

  const [kind, setKind] = useState<'person' | 'organization'>('person');
  const [relationType, setRelationType] = useState<string>('');
  const [mode, setMode] = useState<'search' | 'create'>('search');
  const [search, setSearch] = useState('');
  const [selectedPatient, setSelectedPatient] = useState<PatientPickerItem | null>(null);
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [cpr, setCpr] = useState('');

  const [orgName, setOrgName] = useState('');
  const [orgEmail, setOrgEmail] = useState('');
  const [orgPhone, setOrgPhone] = useState('');
  const [personFieldsSubmitted, setPersonFieldsSubmitted] = useState(false);
  const [orgFieldsSubmitted, setOrgFieldsSubmitted] = useState(false);
  const fieldMessages = {
    invalidEmail: t('patients.errors.invalid_email'),
    invalidPhone: t('patients.errors.invalid_phone'),
  };
  const personFieldErrors = contactFieldErrorTexts(
    personFieldsSubmitted,
    email,
    phone,
    fieldMessages,
  );
  const orgFieldErrors = contactFieldErrorTexts(
    orgFieldsSubmitted,
    orgEmail,
    orgPhone,
    fieldMessages,
  );

  const trimmedSearch = search.trim();
  const personSearchStarted = trimmedSearch.length > 0;
  const expectedSex = kind === 'person' ? expectedSexForPersonRelation(relationType) : null;
  const personPicker = usePatientPicker(
    kind === 'person' && mode === 'search' && personSearchStarted && !expectedSex ? clinicId : null,
    search,
    { statuses: ['active', 'related'] },
  );
  const genderedPicker = useRelationPersonSearch(
    kind === 'person' && mode === 'search' && personSearchStarted && expectedSex ? clinicId : null,
    trimmedSearch,
    expectedSex,
    patientId,
  );
  const orgKind =
    kind === 'organization'
      ? contactKindFromOrgRelation(relationType as OrgRelationType)
      : undefined;
  const contactsQuery = useClinicContacts(clinicId, {
    search: kind === 'organization' ? search : '',
    kind: orgKind,
  });

  useEffect(() => {
    setSelectedPatient(null);
    setSelectedContactId(null);
    setSearch('');
    if (kind === 'person') {
      setRelationType('');
    } else {
      setRelationType('insurance');
    }
  }, [kind]);

  const personQuery = expectedSex ? genderedPicker : personPicker;
  const personResults = keepSelectedRelationPerson(
    (personQuery.data ?? []).filter((p) => p.id !== patientId),
    personSearchStarted ? selectedPatient : null,
  );
  const contactResults = contactsQuery.data ?? [];

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    try {
      if (!relationType) {
        setSubmitError(t('patients.relations.error_relation_type'));
        return;
      }
      if (kind === 'person') {
        let toPatientId = selectedPatient?.id ?? null;
        if (mode === 'create') {
          if (!firstName.trim() || !lastName.trim()) {
            setSubmitError(t('patients.errors.name_required'));
            return;
          }
          if (canSetCpr && cpr.trim().length > 0 && !isAcceptableCpr(cpr)) {
            setSubmitError(t('patients.errors.cpr_format'));
            return;
          }
          const personFields = contactFieldErrorTexts(true, email, phone, fieldMessages);
          if (!personFields.canSave) {
            setPersonFieldsSubmitted(true);
            setSubmitError(t('patients.errors.invalid_input'));
            focusPatientField(personFields.email ? 'phone' : 'email');
            return;
          }
          const created = await createPatient.mutateAsync({
            clinic_id: clinicId,
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            email: email.trim() || undefined,
            telephone_subscribers: phone.trim()
              ? [{ identifier: phone.trim(), code: DEFAULT_TELEPHONE_CODE }]
              : null,
            cpr: canSetCpr ? cpr.trim() || undefined : undefined,
            status: 'related',
          });
          toPatientId = created.id;
        }
        if (!toPatientId) {
          setSubmitError(t('patients.relations.error_select_person', 'Select or create a person'));
          return;
        }
        await createRelation.mutateAsync({
          from_patient_id: patientId,
          relation_type: relationType,
          to_patient_id: toPatientId,
          notes: notes.trim() || null,
        });
      } else {
        let toContactId = selectedContactId;
        if (mode === 'create') {
          if (!orgName.trim()) {
            setSubmitError(t('patients.relations.error_org_name', 'Enter a name'));
            return;
          }
          const orgFields = contactFieldErrorTexts(true, orgEmail, orgPhone, fieldMessages);
          if (!orgFields.canSave) {
            setOrgFieldsSubmitted(true);
            return;
          }
          const created = await createContact.mutateAsync({
            name: orgName.trim(),
            kind: contactKindFromOrgRelation(relationType as OrgRelationType),
            email: orgEmail.trim() || null,
            phone: orgPhone.trim() || null,
          });
          toContactId = created.id;
        }
        if (!toContactId) {
          setSubmitError(
            t('patients.relations.error_select_org', 'Select or create an organisation'),
          );
          return;
        }
        await createRelation.mutateAsync({
          from_patient_id: patientId,
          relation_type: relationType,
          to_contact_id: toContactId,
          notes: notes.trim() || null,
        });
      }
      toast.success(t('patients.relations.toast_created', 'Relation added'));
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : '';
      if (message.toLowerCase().includes('unique') || message.toLowerCase().includes('duplicate')) {
        setSubmitError(t('patients.relations.error_duplicate', 'This relation already exists'));
      } else {
        setSubmitError(message || t('patients.relations.error_create', 'Could not add relation'));
      }
    }
  };

  const pending = createRelation.isPending || createPatient.isPending || createContact.isPending;
  const types = kind === 'person' ? PERSON_RELATION_TYPES : ORG_RELATION_TYPES;

  return (
    <SlideOver
      open
      title={t('patients.relations.add')}
      onClose={onClose}
      dirty={Boolean(firstName || lastName || orgName || notes || search)}
      footer={
        <SlideOverFooter
          onCancel={onClose}
          primary={
            <Button type="submit" form="add-relation-form" disabled={pending} loading={pending}>
              {t('patients.relations.add')}
            </Button>
          }
        />
      }
    >
      <form id="add-relation-form" onSubmit={onSubmit} className="space-y-s4">
        <div>
          <p className={cn(TYPE.label, 'text-ds-text')}>{t('patients.relations.kind_label')}</p>
          <SegmentedControl
            className="mt-s2 w-full"
            ariaLabel={t('patients.relations.kind_label')}
            value={kind}
            onChange={setKind}
            options={[
              { value: 'person', label: t('patients.relations.kind_person') },
              { value: 'organization', label: t('patients.relations.kind_org') },
            ]}
          />
        </div>

        <Select
          id="relation-type"
          label={t('patients.relations.relation_type')}
          value={relationType}
          onChange={(e) => setRelationType(e.target.value)}
          required
        >
          {kind === 'person' ? (
            <option value="">{t('patients.relations.relation_placeholder')}</option>
          ) : null}
          {types.map((type) => (
            <option key={type} value={type}>
              {t(`patients.relations.types.${type}`, type)}
            </option>
          ))}
        </Select>

        <SegmentedControl
          className="w-full"
          ariaLabel={t('patients.relations.mode_search')}
          value={mode}
          onChange={setMode}
          options={[
            { value: 'search', label: t('patients.relations.mode_search') },
            { value: 'create', label: t('patients.relations.mode_create') },
          ]}
        />

        {mode === 'search' && (
          <div>
            <Input
              label={
                kind === 'person'
                  ? t('patients.relations.search_person', 'Search person')
                  : t('patients.relations.search_org', 'Search organisation')
              }
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setSelectedPatient(null);
                setSelectedContactId(null);
              }}
              placeholder={t('patients.relations.search_placeholder', 'Type to search…')}
            />
            <div className="mt-s2 max-h-40 overflow-y-auto rounded-ctl border border-ds-field">
              {kind === 'person' ? (
                !personSearchStarted ? (
                  <p className="px-s3 py-s2 text-sm text-ds-muted" role="status">
                    {t('patients.relations.search_type_to_list')}
                  </p>
                ) : (
                  <>
                    {personResults.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setSelectedPatient(p)}
                        className={`block w-full px-3 py-2 text-left text-sm ${
                          selectedPatient?.id === p.id ? 'bg-ds-teal-tint' : 'hover:bg-ds-sunk'
                        }`}
                      >
                        <span className="font-medium text-ds-text">
                          {p.first_name} {p.last_name}
                        </span>
                        {(p.phone ||
                          p.email ||
                          formatPatientTelephoneNumbers(p.telephone_subscribers, p.phone)) && (
                          <span className="ml-2 text-ds-muted">
                            {[
                              formatPatientTelephoneNumbers(p.telephone_subscribers, p.phone),
                              p.email,
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </span>
                        )}
                      </button>
                    ))}
                    {personQuery.isLoading ? (
                      <p className="px-s3 py-s2 text-sm text-ds-muted">{t('topbar.searching')}</p>
                    ) : null}
                    {personQuery.isError ? (
                      <p className="px-s3 py-s2 text-sm text-ds-muted" role="alert">
                        {t('patients.relations.search_error')}
                      </p>
                    ) : null}
                    {!personQuery.isLoading &&
                    !personQuery.isError &&
                    personResults.length === 0 ? (
                      <p className="px-s3 py-s2 text-sm text-ds-muted">
                        {t('patients.relations.no_matches')}
                      </p>
                    ) : null}
                  </>
                )
              ) : contactsQuery.isLoading ? (
                <p className="px-s3 py-s2 text-sm text-ds-muted">{t('topbar.searching')}</p>
              ) : contactResults.length === 0 ? (
                <p className="px-s3 py-s2 text-sm text-ds-muted">
                  {t('patients.relations.no_matches')}
                </p>
              ) : (
                contactResults.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setSelectedContactId(c.id)}
                    className={`block w-full px-3 py-2 text-left text-sm ${
                      selectedContactId === c.id ? 'bg-ds-teal-tint' : 'hover:bg-ds-sunk'
                    }`}
                  >
                    <span className="font-medium text-ds-text">{c.name}</span>
                    {(c.phone || c.email) && (
                      <span className="ml-2 text-ds-muted">
                        {[c.phone, c.email].filter(Boolean).join(' · ')}
                      </span>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        )}

        {mode === 'create' && kind === 'person' && (
          <div className="space-y-3">
            <Input
              label={t('patients.field_first_name')}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
            />
            <Input
              label={t('patients.field_last_name')}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
            />
            <Input
              label={t('patients.field_phone')}
              name="phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              error={personFieldErrors.phone}
            />
            <Input
              label={t('patients.field_email')}
              name="email"
              type="text"
              inputMode="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={personFieldErrors.email}
            />
            {canSetCpr && (
              <Input
                label={t('patients.field_cpr')}
                value={cpr}
                onChange={(e) => setCpr(e.target.value)}
              />
            )}
          </div>
        )}

        {mode === 'create' && kind === 'organization' && (
          <div className="space-y-3">
            <Input
              label={t('patients.relations.org_name', 'Name')}
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              required
            />
            <Input
              label={t('patients.field_phone')}
              name="org_phone"
              type="tel"
              value={orgPhone}
              onChange={(e) => setOrgPhone(e.target.value)}
              error={orgFieldErrors.phone}
            />
            <Input
              label={t('patients.field_email')}
              name="org_email"
              type="text"
              inputMode="email"
              autoComplete="email"
              value={orgEmail}
              onChange={(e) => setOrgEmail(e.target.value)}
              error={orgFieldErrors.email}
            />
          </div>
        )}

        <Input
          label={t('patients.relations.notes', 'Notes')}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />

        {submitError && <p className={cn(TYPE.small, 'text-ds-red-ink')}>{submitError}</p>}
      </form>
    </SlideOver>
  );
}

function EditRelatedPersonModal({
  relation,
  onClose,
}: {
  relation: PatientRelationRow;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const update = useUpdatePatient();
  const nameParts = (relation.target_name ?? '').split(' ');
  const [firstName, setFirstName] = useState(nameParts[0] ?? '');
  const [lastName, setLastName] = useState(nameParts.slice(1).join(' '));
  const [email, setEmail] = useState(relation.target_email ?? '');
  const [phone, setPhone] = useState(relation.target_phone ?? '');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [fieldsSubmitted, setFieldsSubmitted] = useState(false);
  const fieldMessages = {
    invalidEmail: t('patients.errors.invalid_email'),
    invalidPhone: t('patients.errors.invalid_phone'),
  };
  const fieldErrors = contactFieldErrorTexts(fieldsSubmitted, email, phone, fieldMessages);

  if (!relation.to_patient_id) return null;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    const fields = contactFieldErrorTexts(true, email, phone, fieldMessages);
    if (!fields.canSave) {
      setFieldsSubmitted(true);
      setSubmitError(t('patients.errors.invalid_input'));
      focusPatientField(fields.email ? 'phone' : 'email');
      return;
    }
    try {
      await update.mutateAsync({
        patient_id: relation.to_patient_id as string,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim() || null,
        telephone_subscribers: phone.trim()
          ? [{ identifier: phone.trim(), code: DEFAULT_TELEPHONE_CODE }]
          : null,
      });
      toast.success(t('patients.toast_updated'));
      onClose();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : t('patients.errors.unknown'));
    }
  };

  const convertToPatient = async () => {
    try {
      await update.mutateAsync({
        patient_id: relation.to_patient_id as string,
        status: 'active',
      });
      toast.success(t('patients.relations.toast_converted', 'Now a patient'));
      onClose();
      navigate(`/patients/${relation.to_patient_id}`);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : t('patients.errors.unknown'));
    }
  };

  return (
    <SlideOver
      open
      title={relation.target_name ?? ''}
      onClose={onClose}
      dirty={firstName.length + lastName.length + email.length + phone.length > 0}
      footer={
        <SlideOverFooter
          onCancel={onClose}
          primary={
            <Button
              type="submit"
              form="edit-relation-form"
              disabled={update.isPending}
              loading={update.isPending}
            >
              {t('common.save')}
            </Button>
          }
        />
      }
    >
      <form id="edit-relation-form" onSubmit={onSubmit} className="space-y-s4">
        <Input
          label={t('patients.field_first_name')}
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          required
        />
        <Input
          label={t('patients.field_last_name')}
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          required
        />
        <Input
          label={t('patients.field_phone')}
          name="phone"
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          error={fieldErrors.phone}
        />
        <Input
          label={t('patients.field_email')}
          name="email"
          type="text"
          inputMode="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={fieldErrors.email}
        />
        {submitError && <p className={cn(TYPE.small, 'text-ds-red-ink')}>{submitError}</p>}
        <Button
          type="button"
          variant="secondary"
          onClick={convertToPatient}
          disabled={update.isPending}
        >
          {t('patients.relations.convert')}
        </Button>
      </form>
    </SlideOver>
  );
}

export function RelatedPersonNotice({
  onConvert,
  converting,
}: {
  onConvert: () => void;
  converting: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Banner
      tone="warning"
      title={t('patients.relations.related_notice_title')}
      action={
        <Button type="button" onClick={onConvert} disabled={converting} loading={converting}>
          {t('patients.relations.convert')}
        </Button>
      }
    >
      {t('patients.relations.related_notice_body')}
    </Banner>
  );
}
